import { isEmpty } from 'lodash'
import {
  __,
  or,
  pipe,
  mergeRight,
  pick,
  evolve,
  dissocPath,
  prop,
  propEq,
  omit,
  set,
  lensPath,
  path,
  defaultTo,
  mergeDeepLeft,
  reject,
  anyPass,
  mergeDeepRight
} from 'ramda'
import { batch } from 'react-redux'
import selectorBuilder from '@/models/selector-builder'
import { headObj } from '@/utils'
import queryClient from '@/components/query-client'
import { pushToDatalayer } from '@/utils/push-datalayer'
import {
  getApplication,
  getApplicationNoLoading,
  getApplications,
  createApplication,
  deleteApplication,
  copyApplication,
  favouriteApplication,
  unfavouriteApplication,
  addExternal,
  addExternalRenewal,
  updateTrustScoreFlags,
  getApplicationTrustScore,
  viewApplication
} from './ports'
import { applicationSelector, contractDataSelector } from './selectors'

const formatApplicationFields = pipe(
  pick(['move_in_date']),
  evolve({
    move_in_date: or(__, null)
  })
)

const parseCreateApplication = raw => {
  const livScore = prop('liv_score', raw)
  const data = omit(['liv_score'], raw)
  const obj = pipe(headObj)(data)
  return set(lensPath([obj.id, 'liv_score']), livScore, data)
}

const getFile = (verificationFile, userSecureFilesArray) => {
  const fileUploaded = userSecureFilesArray.find(
    file => file.tag === verificationFile && file.deleted === '0'
  )
  const fileDeleted = userSecureFilesArray.find(
    file => file.tag === verificationFile && file.deleted === '1'
  )
  if (!fileDeleted && !fileUploaded) return ''
  return fileUploaded ?? fileDeleted
}

const getTrustScoreFiles = user => {
  const userSecureFiles = path(['files', 'user_secure_files'])(user)
  const userSecureFilesArray = Object.values(userSecureFiles)
  const incomeVerification = getFile('income_verification', userSecureFilesArray)
  const isStudent = user.job_title === 'student'
  const schoolVerification = isStudent ? getFile('school_verification', userSecureFilesArray) : ''
  const employmentVerification = !isStudent
    ? getFile('employment_verification', userSecureFilesArray)
    : ''
  return {
    school_verification: schoolVerification,
    employment_verification: employmentVerification,
    income_verification: incomeVerification
  }
}

const removeDeletedFiles = pipe(
  path(['files', 'user_secure_files']),
  reject(
    anyPass([
      propEq('deleted', '1'),
      propEq('tag', 'employment_verification'),
      propEq('tag', 'school_verification'),
      propEq('tag', 'income_verification')
    ])
  )
)

const parseApplication = raw => {
  const data = omit(['liv_score'], raw)
  const firstKey = Object.keys(data)[0] // This gets "4495" based on your example
  const users = data.users
  data[firstKey].users = users
  delete data.users
  const obj = headObj(data)
  // current "live" user data, used for avatar/files/livScore which can change after an application is sent
  const user = raw.users[obj.user_info.id]
  const trustScoreFiles = getTrustScoreFiles(user)
  let userSecureFiles = removeDeletedFiles(user)
  userSecureFiles = mergeRight(trustScoreFiles, userSecureFiles)
  const avatar = prop('avatar', user)
  const dataWithAvatar = set(lensPath([obj.id, 'user_info', 'avatar']), avatar, data)
  const internationalUser = prop('international_user', user)
  const indentityVerifiedState = prop('identity_verified_state', user)
  const dataWithInternationalUser = set(
    lensPath([obj.id, 'user_info', 'international_user']),
    internationalUser,
    dataWithAvatar
  )
  const livScore = prop('liv_score', raw)
  const dataWithLivScore = set(lensPath([obj.id, 'liv_score']), livScore, dataWithInternationalUser)
  const dataWithDocs = set(
    lensPath([obj.id, 'user_info', 'files', 'user_secure_files']),
    userSecureFiles,
    dataWithLivScore
  )
  return set(
    lensPath([obj.id, 'user_info', 'identity_verified_state']),
    indentityVerifiedState,
    dataWithDocs
  )
}

const INITIAL_STATE = {}

const application = {
  state: INITIAL_STATE,
  reducers: {
    clear: () => INITIAL_STATE,
    remove: (state, at) => dissocPath(at, state),
    patch: (state, at, payload) =>
      set(lensPath(at), pipe(path(at), defaultTo({}), mergeDeepLeft(payload))(state), state),
    merge: mergeDeepRight
  },

  effects: dispatch => ({
    create: async ({ listingId, message, ...payload }) => {
      try {
        const { error } = await dispatch.chat.startChat({
          message,
          move_in_date: payload.move_in_date || 'asap',
          listingId
        })
        if (error) return { error }
        const { body, response } = await createApplication(
          {
            body: formatApplicationFields(payload)
          },
          { listingId }
        )
        if (!response.ok) {
          throw Error(response.body.message || response.statusText)
        }
        const cleanedBody = pipe(omit('liv_score'), headObj)(body)
        batch(() => {
          dispatch.application.merge(parseCreateApplication(body))
          dispatch.chat.updateApplication(cleanedBody)
        })
        return { body: headObj(body), response }
      } catch (error) {
        return { error }
      }
    },
    delete: async applicationId => {
      try {
        const { response } = await deleteApplication(undefined, {
          applicationId
        })
        if (!response.ok) {
          throw Error(response.body.message || response.statusText)
        }
        dispatch.application.remove([applicationId])
        return {}
      } catch (error) {
        return { error }
      }
    },
    load: async (applicationId, rootState) => {
      if (rootState.application[applicationId]) return
      try {
        const { body, response } = await getApplication({
          applicationId
        })
        if (!response.ok) {
          throw Error(response.body.message || response.statusText)
        }
        dispatch.application.merge(parseApplication(body))

        pushToDatalayer({
          event: 'loadApplication',
          applicationId
        })
      } catch (error) {
        return { error }
      }
    },
    loadNoLoading: async (applicationId, rootState) => {
      const data = omit(['trust_score'], rootState.application[applicationId]) ?? {}
      if (!isEmpty(data)) return
      dispatch.application.patch([applicationId], { loading: true })
      try {
        const { body, response } = await getApplicationNoLoading({
          applicationId
        })
        if (!response.ok) {
          throw Error(response.body.message || response.statusText)
        }

        batch(() => {
          dispatch.application.merge(parseApplication(body))
          dispatch.application.patch([applicationId], { loading: false })
        })
        pushToDatalayer({
          event: 'loadApplication',
          applicationId
        })
      } catch (error) {
        dispatch.application.patch([applicationId], { loading: false })
        return { error }
      }
    },
    getByListingId: async (listingId, rootState) => {
      try {
        const { body, response } = await getApplications({
          listingId
        })
        if (!response.ok) {
          throw Error(response.body.message || response.statusText)
        }
        const applications = parseApplication(body?.applications)
        dispatch.application.merge(applications)
        return { applications }
      } catch (error) {
        return { error }
      }
    },
    confirmCopy: async ({ targetListingId, applicationId }) => {
      try {
        const { response, body } = await copyApplication(
          {
            body: { listing_id: targetListingId }
          },
          { applicationId }
        )
        if (!response.ok) {
          throw Error(response.body.message || response.statusText)
        }
        return { response, body }
      } catch (error) {
        return { error }
      }
    },
    favourite: async applicationId => {
      try {
        const { response, body } = await favouriteApplication(undefined, {
          applicationId
        })
        if (!response.ok) {
          throw Error(response.body.message || response.statusText)
        }
        dispatch.listing.setValue(
          ['listing_applications', 'applications', applicationId, 'favourite'],
          body[applicationId]?.favourite
        )
        batch(() => {
          dispatch.listing.setValue(
            ['listing_applications', 'applications', applicationId, 'favourite'],
            body[applicationId]?.favourite
          )
          dispatch.application.merge(body)
          dispatch.application.patch([applicationId], { favourite: '1' })
        })
        return { response, body }
      } catch (error) {
        return { error }
      }
    },
    unfavourite: async applicationId => {
      try {
        const { response, body } = await unfavouriteApplication(undefined, {
          applicationId
        })
        if (!response.ok) {
          throw Error(response.body.message || response.statusText)
        }
        dispatch.listing.setValue(
          ['listing_applications', 'applications', applicationId, 'favourite'],
          body[applicationId]?.favourite
        )
        batch(() => {
          dispatch.listing.setValue(
            ['listing_applications', 'applications', applicationId, 'favourite'],
            body[applicationId]?.favourite
          )
          dispatch.application.merge(body)
          dispatch.application.patch([applicationId], { favourite: '0' })
        })
        return { response, body }
      } catch (error) {
        return { error }
      }
    },
    addExternal: async payload => {
      const { data, isExtension } = payload
      const addExternalPort = isExtension ? addExternalRenewal : addExternal
      try {
        const { response, body } = await addExternalPort({ body: data })
        if (!response.ok) {
          throw Error(response.body.message || response.statusText)
        }
        batch(() => {
          dispatch.listing.patch(['listing_applications', 'applications'], body.applications)
          dispatch.listing.patch(['listing_applications', 'users'], body.users)
        })
        return { response, body }
      } catch (error) {
        return { error }
      }
    },
    updateTrustScoreFlags: async ({ applicationId, payload }) => {
      try {
        const { response, body } = await updateTrustScoreFlags(
          {
            body: payload
          },
          { applicationId }
        )
        if (!response.ok) {
          throw Error(response.body.message || response.statusText)
        }
        dispatch.application.patch([applicationId], body)
        queryClient.invalidateQueries({
          queryKey: ['companyManagement', 'blocks', 'credit_checks']
        })
      } catch (error) {
        return { error }
      }
    },
    getApplicationTrustScore: async applicationId => {
      dispatch.application.patch([applicationId, 'trust_score'], { loading: true })
      try {
        const { body, response } = await getApplicationTrustScore({ applicationId })
        if (!response.ok) {
          throw Error(response.body.message || response.statusText)
        }

        pushToDatalayer({
          event: 'loadApplicationTrustScore',
          trustScoreState: body.state,
          trustScoreStatus: body.status
        })

        dispatch.application.patch([applicationId, 'trust_score'], { ...body, loading: false })
        return { body, response }
      } catch (error) {
        dispatch.application.patch([applicationId, 'trust_score'], { loading: false })
        return { error }
      }
    },

    viewApplication: async applicationId => {
      try {
        const { response } = await viewApplication(
          {},
          {
            applicationId
          }
        )
        if (!response.ok) {
          throw Error(response.body.message || response.statusText)
        }
      } catch (error) {
        return { error }
      }
    }
  }),

  selectors: selectorBuilder(slice => ({
    applicationDetail() {
      return slice(applicationSelector)
    },
    contractData() {
      return slice(contractDataSelector)
    }
  }))
}

export default application
